<template>
  <div class="admin-page event-workbench">
    <!-- 工作台顶栏 -->
    <div class="workbench-header">
      <div style="display:flex; align-items:center; gap:16px;">
        <a-button size="large" icon="arrow-left" @click="goBack">返回赛事</a-button>
        <div>
          <span style="color:#64748b; font-size:14px; display:block;">赛事管理工作台</span>
          <h2 style="margin:0; font-size:22px; font-weight:700; color:#0f172a;">
            {{ eventTitle || '赛事加载中...' }}
          </h2>
        </div>
      </div>
      <div v-if="eventInfo" style="display:flex; align-items:center; gap:8px;">
        <a-tag :color="statusColor(eventInfo.status)">{{ !acceptsRegistration && [1, 4].includes(eventInfo.status) ? rt('upcoming') : statusText(eventInfo.status) }}</a-tag>
        <span style="color:#64748b; font-size:14px;">地点：{{ eventInfo.location || '待定' }}</span>
      </div>
    </div>

    <!-- 工作台主体 Tab 页 -->
    <div class="workbench-content">
      <a-tabs v-if="eventInfo" :active-key="activeTabKey" size="large" :tabBarStyle="{ paddingLeft: '24px', margin: 0, borderBottom: '1px solid #f1f5f9' }" @change="onTabChange">
        <a-tab-pane key="event-entries" :tab="rt('entries')">
          <div v-if="activeTabKey === 'event-entries'" style="padding:16px 24px">
            <unified-participants :key="eventId + '-' + registrationReloadKey" :event="eventInfo" :categories="categories" @add-registration="handleAddEventTeam" />
          </div>
        </a-tab-pane>
        <a-tab-pane key="results-quick-entry" :tab="rt('workspace')">
          <template>
          <a-alert type="info" show-icon message="在卡片内编排和录分，点击保存全部：空成绩保留赛程，已填写成绩同步展示；修改公开成绩需填写更正原因。" style="margin:0 24px 16px;" />
          <!-- 筛选排版 -->
          <div class="filter-bar">
            <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
              <div>
                <span class="filter-label">组别</span>
                <a-select v-model="filter.categoryId" :disabled="loading || allSaving || hasRoundEdits" allowClear placeholder="全部组别" style="width:180px;" @change="handleFilterChange">
                  <a-select-option v-for="item in categories" :key="item.id" :value="item.id">
                    {{ item.categoryName }}
                  </a-select-option>
                </a-select>
              </div>

              <div>
                <span class="filter-label">阶段</span>
                <a-select v-model="filter.roundName" :disabled="loading || allSaving || hasRoundEdits" allowClear placeholder="全部阶段" style="width:140px;" @change="handleFilterChange">
                  <a-select-option v-for="item in activeStageOptions" :key="item.stageName" :value="item.stageName">
                    {{ item.stageName }}
                  </a-select-option>
                </a-select>
              </div>

              <div>
                <span class="filter-label">距离</span>
                <a-select v-model="filter.distance" :disabled="loading || allSaving || hasRoundEdits" allowClear placeholder="全部距离" style="width:140px;" @change="handleFilterChange">
                  <a-select-option v-for="item in activeDistanceOptions" :key="item.distance" :value="item.distance">
                    {{ item.distance }} 米
                  </a-select-option>
                </a-select>
              </div>

              <div>
                <span class="filter-label">状态</span>
                <a-select v-model="filter.status" :disabled="loading || allSaving || hasRoundEdits" allowClear placeholder="全部状态" style="width:140px;" @change="handleFilterChange">
                  <a-select-option :value="0">准备中</a-select-option>
                  <a-select-option :value="1">进行中</a-select-option>
                  <a-select-option :value="2">已结束</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                </a-select>
              </div>

              <a-button type="primary" icon="search" :disabled="loading || allSaving || hasRoundEdits" style="margin-top:20px;" @click="loadRoundsAndResults">查询</a-button>
            </div>

            <!-- 控制按钮组 -->
            <div style="display:flex; gap:8px; margin-top:20px; flex-wrap:wrap;">
              <a-button type="primary" icon="save" :loading="allSaving" :disabled="roundDataIncomplete || importing" @click="handleSaveAllRounds">
                保存全部
              </a-button>
              <a-button icon="sort-ascending" @click="handleSortRoundsByTime">
                {{ timeSortOrder === 'asc' ? '时间正序' : '时间倒序' }}
              </a-button>
              <a-button type="primary" class="btn-success" :disabled="roundDataIncomplete || allSaving" @click="handleAddNewRound">
                添加
              </a-button>
               <a-button icon="download" @click="downloadWorkbookTemplate">
                 下载赛程成绩模板
               </a-button>
               <a-button icon="export" @click="handleExportResults" :loading="exporting">
                 导出赛程成绩
               </a-button>
              <a-upload
                :customRequest="handleWorkbookImport"
                :showUploadList="false"
                accept=".xls,.xlsx"
                style="display:inline-block;"
              >
                <a-button icon="upload" :loading="importing" :disabled="allSaving || roundDataIncomplete">导入赛程成绩</a-button>
              </a-upload>
            </div>
          </div>

          <!-- 分组卡片列表（大格子） -->
          <div style="padding:24px; background:var(--jk-surface-raised, #f8fafc); min-height:400px;">
            <div v-if="loading" role="status" style="margin-bottom:16px"><a-icon type="loading" /> 正在读取赛程…</div>
            <a-alert v-if="roundLoadError || failedRounds.length" type="warning" show-icon message="赛程尚未完整加载，不代表没有比赛数据；保存、添加和导入暂不可用。" style="margin-bottom:16px">
              <div slot="description">
                <p v-if="roundLoadError">{{ roundLoadError }}</p>
                <div v-for="failure in failedRounds" :key="failure.round.id" style="margin:8px 0">
                  {{ failure.round.raceNo ? `Race ${failure.round.raceNo}` : getRoundTitle(failure.round) }}：{{ failure.message }}
                </div>
                <a-button :loading="loading" :disabled="loading || allSaving" @click="loadRoundsAndResults">重新读取整表</a-button>
              </div>
            </a-alert>
            <p v-if="hasRoundEdits" style="color:var(--jk-text-secondary, #64748b)">有未保存的修改，请先保存再查询。</p>
            <div v-if="editableRoundCards.length > 0" :inert="(roundDataIncomplete || allSaving) ? '' : null">
              <div v-for="round in editableRoundCards" :key="round.id || round.tempId" class="round-card-box" :inert="round.saveNeedsSync ? '' : null">
                <!-- 大格子头部 -->
                <div class="round-card-header">
                  <div class="header-left">
                    <span class="group-badge">{{ round.raceNo ? `Race ${round.raceNo}` : '新增 Race' }}</span>
                    <span class="group-title">{{ getRoundTitle(round) }}</span>
                  </div>
                  
                  <div class="header-right">
                    <div class="meta-field">
                      <span class="meta-label">Race No.</span>
                      <a-input-number
                        v-model="round.raceNo"
                        :min="1"
                        :disabled="round.published === 1"
                        placeholder="自动"
                        style="width:90px;"
                      />
                    </div>
                    <!-- 时间 -->
                    <div class="meta-field">
                      <span class="meta-label">时间</span>
                      <a-date-picker
                        v-model="round.roundTimeStr"
                        show-time
                        format="YYYY-MM-DD HH:mm"
                        value-format="YYYY-MM-DD HH:mm"
                        placeholder="选择比赛时间"
                        style="width:170px;"
                      />
                    </div>
                    <!-- 组别 -->
                    <div class="meta-field">
                      <span class="meta-label">组别</span>
                      <a-select v-if="!round.results.some(row => row.id)" v-model="round.categoryId" style="width:140px;" @change="() => handleRoundCategoryChange(round)">
                        <a-select-option v-for="item in categories" :key="item.id" :value="item.id">
                          {{ item.categoryName }}
                        </a-select-option>
                      </a-select>
                      <span v-else class="meta-readonly">{{ categoryNameOf(round.categoryId) || '未设置' }}</span>
                    </div>
                    <!-- 阶段 -->
                    <div class="meta-field">
                      <span class="meta-label">阶段</span>
                      <a-select v-model="round.roundName" style="width:110px;" @change="() => handleRoundStageChange(round)">
                        <a-select-option v-for="item in activeStageOptions" :key="item.stageName" :value="item.stageName">
                          {{ item.stageName }}
                        </a-select-option>
                      </a-select>
                    </div>
                    <div class="meta-field">
                      <span class="meta-label">組內輪次</span>
                      <a-input-number v-model="round.heatNo" :min="1" style="width:90px;" />
                    </div>
                    <!-- 距离 -->
                    <div class="meta-field">
                      <span class="meta-label">距离</span>
                      <a-select v-model="round.distance" style="width:110px;">
                        <a-select-option v-for="item in activeDistanceOptions" :key="item.distance" :value="item.distance">
                          {{ item.distance }} 米
                        </a-select-option>
                      </a-select>
                    </div>
                    <!-- 状态 -->
                    <div class="meta-field">
                      <span class="meta-label">状态</span>
                      <a-select v-model="round.status" style="width:110px;">
                        <a-select-option :value="0">准备中</a-select-option>
                        <a-select-option :value="1">进行中</a-select-option>
                        <a-select-option :value="2">已结束</a-select-option>
                        <a-select-option :value="3">已取消</a-select-option>
                      </a-select>
                    </div>
                    <!-- 备注 -->
                    <div class="meta-field">
                      <a-input v-model="round.description" placeholder="本组备注：如重赛、风浪、改道" style="width:240px;" />
                    </div>

                    <a-button type="primary" size="small" @click="handleAddNewResultRow(round)">添加</a-button>
                    <a-button v-if="round.published === 1" size="small" :disabled="allSaving" @click="handleWithdrawPublish(round)">撤回发布</a-button>
                    <a-popconfirm title="删除未曾公开的场次及其草稿对阵？已公开成绩与队员协作记录会保留。" ok-text="确定" cancel-text="取消" @confirm="handleDeleteRound(round)">
                      <a-button size="small" class="btn-round-delete" :disabled="roundDataIncomplete || allSaving">删除</a-button>
                    </a-popconfirm>
                  </div>
                </div>

                <a-input v-if="round.published === 1" v-model="round.correctionReason" :max-length="500" placeholder="修改公开成绩时填写更正原因，保留历史版本" style="margin:12px;width:calc(100% - 24px)" />
                <!-- 大格子表格主体 -->
                <div class="round-table-container">
                  <table class="rapid-entry-table">
                    <thead>
                      <tr>
                        <th style="width: 100px;">名次</th>
                        <th style="width: 100px;">航道</th>
                        <th style="min-width: 240px;">队伍/单位</th>
                        <th style="width: 140px;">编号</th>
                        <th style="width: 110px; min-width: 110px;">成绩</th>
                        <th style="width: 120px;">差值</th>
                        <th style="width: 180px;">状态</th>
                        <th style="width: 260px;">备注</th>
                        <th style="width: 90px; text-align:center;">操作</th>
                      </tr>
                    </thead>
                    <draggable
                      v-model="round.results"
                      tag="tbody"
                      handle=".drag-handle"
                      @end="handleResultDragEnd(round)"
                    >
                      <tr v-for="(row, rowIdx) in round.results" :key="row.id || row.tempId || rowIdx" :class="{ 'row-foul-dns': row.isFoul || row.isDns }">
                        <!-- 名次 -->
                        <td>
                          <span class="rank-badge" :class="rankBadgeClass(row.rankNo)">
                            {{ formatRankNo(row.rankNo, row.isFoul || row.isDns) }}
                          </span>
                        </td>
                        <!-- 航道号 -->
                        <td>
                          <a-input-number v-model="row.laneNo" :min="1" style="width:100%; text-align:center;" />
                        </td>
                        <!-- 队伍/单位 (限制只能添加已报名的队伍) -->
                        <td>
                          <a-select
                            v-if="row.published !== 1"
                            v-model="row.eventEntryId"
                            show-search
                            placeholder="输入或选择参赛队"
                            option-filter-prop="children"
                            style="width: 100%;"
                            @change="(val) => handleRowTeamChange(row, val, round)"
                          >
                            <a-select-option v-for="team in round.participantTeams" :key="team.id" :value="team.id">
                              {{ team.teamName }} · {{ team.entryCode }}
                            </a-select-option>
                          </a-select>
                          <span v-else class="cell-readonly">{{ teamLabel(round, row) }}</span>
                        </td>
                        <!-- 参赛编号 -->
                        <td>
                          <a-input v-model="row.bibNo" placeholder="编号" />
                        </td>
                        <!-- 成绩 -->
                        <td>
                          <a-input
                            v-model="row.resultTime"
                            placeholder="例如 2:11.32"
                            :disabled="row.isFoul || row.isDns"
                            @blur="handleRecalculateRound(round, false)"
                            @pressEnter="handleRecalculateRound(round, false)"
                          />
                        </td>
                        <!-- 差值 -->
                        <td>
                          <span :class="gapTimeClass(row.rankNo, row.isFoul || row.isDns)">
                            {{ formatGapText(row.gapTime, row.isFoul || row.isDns) }}
                          </span>
                        </td>
                        <!-- 状态 (犯规/DNS) -->
                        <td>
                          <a-checkbox v-model="row.isFoul" @change="handleRowStatusChange(row, 'foul', round)">犯规</a-checkbox>
                          <a-checkbox v-model="row.isDns" @change="handleRowStatusChange(row, 'dns', round)">DNS</a-checkbox>
                        </td>
                        <!-- 备注 -->
                        <td>
                          <a-input v-model="row.remark" placeholder="备注信息" style="width:100%;" />
                        </td>
                        <!-- 操作 -->
                        <td class="row-action-cell">
                          <a-icon type="drag" class="drag-handle" title="拖动调整顺序" />
                          <a-icon v-if="row.published !== 1" type="delete" style="color:red; cursor:pointer; font-size:16px;" @click="handleRemoveRow(round, rowIdx)" />
                        </td>
                      </tr>
                    </draggable>
                    <tbody v-if="round.results.length === 0">
                      <tr v-if="round.results.length === 0">
                        <td colspan="9" style="text-align:center; padding: 24px 0; color:#94a3b8;">
                          暂无队伍，请点击右上角“添加”排定航道。
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- 大格子底部动作 -->
                <div class="round-card-footer">
                  <div class="footer-left">
                    <span style="margin-right:16px;">最快成绩：<strong>{{ getRoundFastestTime(round) }}</strong></span>
                    <span>已录队伍：<strong>{{ round.results.length }}</strong></span>
                  </div>
                  <div class="footer-right">
                    <a-button size="small" style="border-radius:6px;" @click="openRoundLineup(round)">坐舱名单</a-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="!roundDataIncomplete" style="padding: 80px 0; text-align: center; background: var(--jk-surface, #fff); border-radius: 12px;">
              <a-empty description="当前筛选下暂无比赛场次。可调整筛选，或点击上方“添加”录入。" />
            </div>
          </div>
          </template>
        </a-tab-pane>
        <a-tab-pane key="results-center" :tab="centerTitle">
          <div class="unified-panel">
            <p class="unified-note">{{ rt('centerHelp') }}</p>
            <a-radio-group v-model="centerView" button-style="solid" class="unified-switch">
              <a-radio-button value="points">{{ rt('points') }}</a-radio-button>
              <a-radio-button value="classification">{{ rt('classification') }}</a-radio-button>
            </a-radio-group>
            <dragon-scoring v-if="activeTabKey === 'results-center' && centerView === 'points'" :event-id="Number(eventId)" embedded />
            <results-center v-if="activeTabKey === 'results-center' && centerView === 'classification'" :key="eventId" :event="eventInfo" :categories="categories" />
          </div>
        </a-tab-pane>
        <a-tab-pane key="category-config" tab="组别配置">
          <div style="padding:24px;">
            <div style="margin-bottom:20px; display:flex; justify-content:flex-end; align-items:center;">
              <a-button type="primary" icon="plus" @click="handleAddCategory">新增组别</a-button>
            </div>
            <a-table
              rowKey="id"
              :columns="visibleCategoryColumns"
              :data-source="categories"
              :loading="categoryLoading"
              :pagination="false"
              style="background:var(--jk-surface, #fff);"
            >
              <span slot="status" slot-scope="text">
                <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '启用' : '禁用' }}</a-tag>
              </span>
              <span class="admin-row-actions" slot="action" slot-scope="text, record">
                <a-button type="link" size="small" icon="edit" class="table-action-button" @click="handleEditCategory(record)">编辑</a-button>
                <a-popconfirm title="确定删除此组别吗？这会级联清除此组别相关的成绩配置！" ok-text="确定" cancel-text="取消" @confirm="handleDeleteCategory(record.id)">
                  <a-button type="link" size="small" icon="delete" class="table-action-button table-action-danger">删除</a-button>
                </a-popconfirm>
              </span>
            </a-table>
          </div>
        </a-tab-pane>
        <a-tab-pane key="stage-config" :tab="$t('stageConfig')">
          <div style="padding:24px;">
            <div style="margin-bottom:20px; display:flex; justify-content:flex-end; align-items:center;">
              <a-button type="primary" icon="plus" @click="handleAddStage">{{ $t('stageAdd') }}</a-button>
            </div>
            <a-table
              rowKey="id"
              :columns="stageColumns"
              :data-source="stageOptions"
              :loading="stageLoading"
              :pagination="false"
              style="background:var(--jk-surface, #fff);"
            >
              <span slot="status" slot-scope="text">
                <a-tag :color="text === 1 ? 'green' : 'red'">{{ $t(text === 1 ? 'stageEnabled' : 'stageDisabled') }}</a-tag>
              </span>
              <span class="admin-row-actions" slot="action" slot-scope="text, record">
                <a-button type="link" size="small" icon="edit" class="table-action-button" @click="handleEditStage(record)">{{ $t('stageEdit') }}</a-button>
                <a-popconfirm :title="$t('stageDeleteConfirm')" @confirm="handleDeleteStage(record.id)">
                  <a-button type="link" size="small" icon="delete" class="table-action-button table-action-danger">{{ $t('stageDelete') }}</a-button>
                </a-popconfirm>
              </span>
            </a-table>
          </div>
        </a-tab-pane>
        <a-tab-pane key="distance-config" tab="距离配置">
          <div style="padding:24px;">
            <div style="margin-bottom:20px; display:flex; justify-content:flex-end; align-items:center;">
              <a-button type="primary" icon="plus" @click="handleAddDistance">新增距离</a-button>
            </div>
            <a-table
              rowKey="id"
              :columns="distanceColumns"
              :data-source="distanceOptions"
              :loading="distanceLoading"
              :pagination="false"
              style="background:var(--jk-surface, #fff);"
            >
              <span slot="distance" slot-scope="text">{{ text }} 米</span>
              <span slot="status" slot-scope="text">
                <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '启用' : '停用' }}</a-tag>
              </span>
              <span class="admin-row-actions" slot="action" slot-scope="text, record">
                <a-button type="link" size="small" icon="edit" class="table-action-button" @click="handleEditDistance(record)">编辑</a-button>
                <a-popconfirm title="确定删除此距离吗？" ok-text="确定" cancel-text="取消" @confirm="handleDeleteDistance(record.id)">
                  <a-button type="link" size="small" icon="delete" class="table-action-button table-action-danger">删除</a-button>
                </a-popconfirm>
              </span>
            </a-table>
          </div>
        </a-tab-pane>


      </a-tabs>
    </div>

    <a-modal v-model="lineupEditing" title="调整本轮座舱" width="920px" :mask-closable="false" :closable="!lineupSaving" :keyboard="!lineupSaving" destroy-on-close>
      <round-lineup-editor v-if="lineupEditor" v-model="lineupEditor" :disabled="lineupSaving" />
      <a-form layout="vertical"><a-form-item label="名单更正原因" required><a-textarea v-model="lineupCorrectionReason" :disabled="lineupSaving" :max-length="500" placeholder="说明核对依据；保留调整前后名单，不更改比赛成绩" /></a-form-item></a-form>
      <template slot="footer">
        <a-button :disabled="lineupSaving" @click="lineupEditing = false">取消</a-button>
        <a-button :loading="lineupSaving" @click="saveRoundSeats(false)">保存名单草稿</a-button>
        <a-button type="primary" :loading="lineupSaving" @click="saveRoundSeats(true)">确认完整名单</a-button>
      </template>
    </a-modal>
    <!-- 坐舱名单模态框 -->
    <a-modal v-model="lineupVisible" :title="lineupTitle" width="760px" :footer="null">
      <div v-if="lineupTeams.length" class="lineup-layout">
        <!-- 左侧：队伍列表 -->
        <div class="lineup-team-list">
          <div class="lineup-team-item"
            :class="{ active: activeLineupTeam && activeLineupTeam.registrationId === team.registrationId }"
            v-for="team in lineupTeams" :key="team.registrationId" @click="activeLineupTeam = team">
            <div class="lineup-team-name">{{ team.teamName || '未命名队伍' }}</div>
            <div class="lineup-team-meta">
              <span v-if="team.laneNo">第{{ team.laneNo }}航道</span>
              <span v-if="team.bibNo">编号 {{ team.bibNo }}</span>
              <span v-else style="color:#CBDCE4;">未分配编号</span>
            </div>
          </div>
        </div>
        <!-- 右侧：单队伍座位图 -->
        <div class="lineup-boat" v-if="activeLineupTeam">
          <div class="boat-head">船 头</div>
          <div class="boat-row" v-for="row in adminBoatRows" :key="'row' + row.rowNo">
            <div class="boat-seat" v-for="seat in row.seats" :key="seat.seatOrder">
              <div class="boat-avatar">{{ seat.member ? seat.member.memberName[0] : '空' }}</div>
              <div class="boat-seat-name">{{ seat.member ? seat.member.memberName : '空位' }}</div>
              <div class="boat-seat-pos">{{ seat.member ? row.rowNo + '行' + seat.sideText : '' }}</div>
              <a-tag v-if="seat.member && seat.member.isLeader === 1" color="cyan" style="margin-top:4px;">领队</a-tag>
            </div>
          </div>
          <div class="boat-tail">船 尾</div>
          <div class="boat-alternates" v-if="activeLineupTeam.alternateMembers && activeLineupTeam.alternateMembers.length">
            <div class="boat-alt-title">替补</div>
            <div class="boat-alt-item" v-for="(m, i) in activeLineupTeam.alternateMembers" :key="'a' + i">{{ i + 1 }}. {{ m.memberName }}</div>
          </div>
          <div class="boat-empty" v-if="!activeLineupTeam.seatMembers.length">该队本轮未安排座舱</div>
          <a-button v-if="lineupRound" type="primary" @click="editRoundSeats">调整本轮座舱</a-button>
        </div>
      </div>
      <div v-if="!lineupTeams.length" style="text-align:center; color:#999; padding:30px 0;">该轮暂无通过审核的报名队伍</div>
    </a-modal>

    <!-- 组别新增/编辑模态框 -->
    <a-modal v-model="categoryVisible" :title="categoryForm.id ? '编辑组别' : '新增组别'" @ok="handleSaveCategory" :confirm-loading="categorySaving" :closable="!categorySaving" :mask-closable="!categorySaving" width="760px" destroyOnClose>
      <a-form-model ref="categoryFormRef" :model="categoryForm" :rules="categoryRules" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <single-source-content ref="categoryContent" :value="categoryForm" :fields="categoryContentFields" kind="category" :event-id="Number(eventId)" />
        <a-alert v-if="!acceptsRegistration" type="info" show-icon :message="rt('noRoster')" style="margin-bottom:20px" />
        <a-form-model-item label="船型规则">
          <span>{{ categoryCrewSummary(categoryForm) }} · {{ substituteRuleLabel(categoryForm) }}</span>
          <a-button type="link" @click="categoryAdvanced = !categoryAdvanced">{{ categoryAdvanced ? '收起设置' : '单独设置' }}</a-button>
        </a-form-model-item>
        <template v-if="categoryAdvanced">
        <a-form-model-item label="船型規格">
          <a-select v-model="categoryForm.boatTypeCode" :disabled="categoryForm.competitionRulesLocked" @change="handleCategoryBoatTypeChange">
            <a-select-option value="STANDARD_20">大龍 / Standard Boat（20划手 + 鼓手 + 舵手）</a-select-option>
            <a-select-option value="SMALL_10">小龍 / Small Boat（10划手 + 鼓手 + 舵手）</a-select-option>
            <a-select-option value="CUSTOM">自定义</a-select-option>
          </a-select>
          <div class="form-tip">{{ categoryCrewSummary(categoryForm) }}<span v-if="categoryForm.competitionRulesLocked">；已有報名，競賽規格已鎖定</span></div>
        </a-form-model-item>
        <template v-if="categoryForm.boatTypeCode === 'CUSTOM'">
          <a-form-model-item label="自定義划手">
            <div style="display:flex; align-items:center; gap:8px;">
              <a-input-number v-model="categoryForm.paddlerRows" :min="1" :disabled="categoryForm.competitionRulesLocked" />
              <span>排 ×</span>
              <a-input-number v-model="categoryForm.paddlersPerRow" :min="1" :max="2" :disabled="categoryForm.competitionRulesLocked" />
              <span>人</span>
            </div>
          </a-form-model-item>
          <a-form-model-item label="鼓手 / 舵手">
            <a-input-number v-model="categoryForm.drummerCount" :min="0" :max="8" :disabled="categoryForm.competitionRulesLocked" /> 鼓手
            <a-input-number v-model="categoryForm.steererCount" :min="0" :max="8" :disabled="categoryForm.competitionRulesLocked" /> 舵手
          </a-form-model-item>
        </template>
        <a-form-model-item label="替補規則">
          <a-radio-group v-model="categoryForm.substituteMode" :disabled="categoryForm.competitionRulesLocked">
            <a-radio value="NONE">不設替補</a-radio>
            <a-radio value="LIMITED">限額</a-radio>
            <a-radio value="UNLIMITED">不限</a-radio>
          </a-radio-group>
          <a-input-number v-if="categoryForm.substituteMode === 'LIMITED'" v-model="categoryForm.substituteLimit" :min="1" :disabled="categoryForm.competitionRulesLocked" style="margin-left:12px;" />
        </a-form-model-item>
        </template>
        <a-form-model-item label="排序" prop="sortOrder">
          <a-input-number v-model="categoryForm.sortOrder" :min="0" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item label="状态" prop="status">
          <a-radio-group v-model="categoryForm.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 添加队伍模态框 -->
    <a-modal v-model="teamVisible" title="添加队伍" @ok="handleSaveEventTeam" width="560px" destroyOnClose>
      <a-form-model :model="teamForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="参赛组别">
          <a-select v-model="teamForm.categoryId" placeholder="请选择组别">
            <a-select-option v-for="item in categories" :key="item.id" :value="item.id">{{ item.categoryName }}</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="选择队伍">
          <a-select v-model="teamForm.teamId" @change="loadRegistrationMemberChoices" show-search placeholder="选择队伍管理中的已审核队伍" option-filter-prop="children">
            <a-select-option v-for="item in availableTeamOptions" :key="item.id" :value="item.id">{{ item.teamName }}</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="本次报名人员">
          <a-select v-model="teamForm.memberIds" mode="multiple" :loading="registrationMembersLoading" :disabled="!teamForm.teamId" placeholder="明确勾选，不自动加入俱乐部全员" option-filter-prop="children">
            <a-select-option v-for="person in registrationMemberChoices" :key="person.id" :value="person.id">{{ person.memberName }} · {{ person.isLeader === 1 ? '随队领队' : person.racePosition }}</a-select-option>
          </a-select>
          <small>领队、教练按随队人员登记，不占划手座位。</small>
        </a-form-model-item>
        <a-form-model-item label="参赛编号">
          <a-input v-model="teamForm.bibNo" placeholder="可选" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 阶段新增/编辑模态框 -->
    <a-modal v-model="stageVisible" :title="$t(stageForm.id ? 'stageEdit' : 'stageAdd')" :confirm-loading="stageSaving" :closable="!stageSaving" :mask-closable="!stageSaving" :keyboard="!stageSaving" :cancel-button-props="{ props: { disabled: stageSaving } }" @ok="handleSaveStage" width="560px" destroyOnClose>
      <a-form-model :model="stageForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <single-source-content v-if="stageVisible" ref="stageContent" :value="stageForm" :fields="stageContentFields" kind="stage" :event-id="eventId" />
        <a-form-model-item :label="$t('stageSort')">
          <a-input-number v-model="stageForm.stageOrder" :disabled="stageSaving" :min="0" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item :label="$t('stageStatus')">
          <a-radio-group v-model="stageForm.status" :disabled="stageSaving">
            <a-radio :value="1">{{ $t('stageEnabled') }}</a-radio>
            <a-radio :value="0">{{ $t('stageDisabled') }}</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 距离新增/编辑模态框 -->
    <a-modal v-model="distanceVisible" :title="distanceForm.id ? '编辑距离' : '新增距离'" @ok="handleSaveDistance" width="560px" destroyOnClose>
      <a-form-model :model="distanceForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="比赛距离">
          <a-input-number v-model="distanceForm.distance" :min="1" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item label="排序">
          <a-input-number v-model="distanceForm.distanceOrder" :min="0" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item label="状态">
          <a-radio-group v-model="distanceForm.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import request from '@/utils/request'
import RoundLineupEditor from './RoundLineupEditor.vue'
import SingleSourceContent from '@/components/SingleSourceContent.vue'
import UnifiedParticipants from './UnifiedParticipants.vue'
import ResultsCenter from './ResultsCenter.vue'
import { centerText } from '@/utils/results-center'
import { resultsText, registrationEnabled } from '@/utils/results-service'
import { positiveRouteId, workbenchTab } from '@/utils/admin-routing'
import { loadRoundQueue, roundLoadMessage, completeRoundList } from '@/utils/round-load'
import { CATEGORY_CONTENT_FIELDS, STAGE_CONTENT_FIELDS, createContentDraft, contentPayload, fieldName } from '@/utils/single-source-content'
import {
  addDragonRegistrationTeam,
  auditDragonRegistration,
  deleteDragonRegistration,
  deleteDragonDistanceConfig,
  deleteDragonRound,
  deleteDragonStageConfig,
  getDragonEventDetail,
  getDragonCategoryList,
  saveDragonCategory,
  deleteDragonCategory,
  getDragonRegistrationList,
  getDragonRegistrationRoundLineups,
  getDragonAdminRoundLineup,
  saveDragonAdminRoundLineup,
  getDragonRoundList,
  saveDragonRound,
  getDragonResultList,
  batchSaveDragonResults,
  publishDragonResult,
  importDragonResultWorkbook,
  exportDragonResults,
  getDragonDistanceConfigList,
  getDragonStageConfigList,
  getDragonTeamList,
  getDragonMemberList,
  saveDragonDistanceConfig,
  saveDragonStageConfig
} from '@/api'

export default {
  components: {
    draggable,
    SingleSourceContent,
    UnifiedParticipants,
    ResultsCenter,
    RoundLineupEditor,
    DragonScoring: () => import('@/views/dragonboat/scoring/index.vue')
  },
  computed: {
    resultRouteContext() { return { categoryId: positiveRouteId(this.$route.query.categoryId), roundId: positiveRouteId(this.$route.query.roundId) } },
    centerTitle() { return centerText(this.$adminLocale, 'title') },
    acceptsRegistration() { return registrationEnabled(this.eventInfo) },
    editableRoundCards() { return this.roundCards },
    hasRoundEdits() { return this.roundCards.some(round => !round.id || round.savedFingerprint !== this.roundFingerprint(round)) },
    roundDataIncomplete() { return this.loading || !this.roundListLoaded || !!this.roundLoadError || this.failedRounds.length > 0 },
    visibleCategoryColumns() {
      return this.categoryColumns
    },
    activeStageOptions() {
      return this.stageOptions.filter(item => item.status === 1)
    },
    activeDistanceOptions() {
      return this.distanceOptions.filter(item => item.status === 1)
    },
    stageColumns() {
      return [
        { title: this.$t('stageName'), dataIndex: 'stageName', width: 200, customRender: (value, record) => record[fieldName('stageName', this.$adminLocale)] || value },
        { title: this.$t('stageSort'), dataIndex: 'stageOrder', width: 100 },
        { title: this.$t('stageStatus'), dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
        { title: this.$t('stageActions'), key: 'action', width: 180, scopedSlots: { customRender: 'action' } }
      ]
    },
    availableTeamOptions() {
      const usedTeamIds = this.eventTeams.filter(item => item.categoryId === this.teamForm.categoryId).map(item => item.teamId)
      return this.teamOptions.filter(item => !usedTeamIds.includes(item.id))
    },
    adminBoatRows() {
      const team = this.activeLineupTeam
      if (!team) return []
      const rows = this.boatSeatRows || []
      const members = (team.seatMembers || []).slice().sort((a, b) => (a.seatOrder || 0) - (b.seatOrder || 0))
      const memberMap = {}
      members.forEach(m => { memberMap[m.seatOrder] = m })
      const boatRows = []
      let seatCursor = 1
      rows.forEach((count, i) => {
        const countNum = Number(count) || 0
        const seats = []
        for (let j = 0; j < countNum; j++) {
          const m = memberMap[seatCursor] || null
          seats.push({
            seatOrder: seatCursor,
            member: m,
            sideText: countNum === 1 ? '中间' : j === 0 ? '左' : '右',
            positionRole: this.lineupSeatRoles[seatCursor - 1] || (m && m.positionRole) || ''
          })
          seatCursor++
        }
        boatRows.push({ rowNo: i + 1, seats })
      })
      return boatRows
    }
  },
  data() {
    return {
      activeTabKey: 'event-entries',
      viewAlive: true,
      scheduleView: 'batch',
      defaultCategoryRule: null,
      categoryAdvanced: false,
      centerView: 'points',
      eventId: undefined,
      eventInfo: null,
      eventTitle: '',
      
      filter: {
        categoryId: undefined,
        roundName: undefined,
        distance: undefined,
        status: undefined
      },
      
      // 赛事队伍配置
      eventTeams: [],
      registrationReloadKey: 0,
      teamOptions: [],
      registrationMemberChoices: [],
      registrationMembersLoading: false,
      teamLoading: false,
      teamVisible: false,
      teamForm: {},
      teamColumns: [
        { title: '组别', dataIndex: 'categoryName', width: 160 },
        { title: '队伍', dataIndex: 'teamName', width: 220 },
        { title: '编号', dataIndex: 'bibNo', width: 120 },
        { title: '状态', dataIndex: 'auditStatus', width: 120, scopedSlots: { customRender: 'auditStatus' } },
        { title: '备注', dataIndex: 'auditRemark' },
        { title: '操作', key: 'action', width: 180, scopedSlots: { customRender: 'action' } }
      ],

      // 阶段配置
      stageOptions: [],
      stageLoading: false,
      stageVisible: false,
      stageSaving: false,
      stageContentFields: STAGE_CONTENT_FIELDS,
      stageForm: {},

      // 距离配置
      distanceOptions: [],
      distanceLoading: false,
      distanceVisible: false,
      distanceForm: {},
      distanceColumns: [
        { title: '比赛距离', dataIndex: 'distance', width: 160, scopedSlots: { customRender: 'distance' } },
        { title: '排序', dataIndex: 'distanceOrder', width: 100 },
        { title: '状态', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', width: 160, scopedSlots: { customRender: 'action' } }
      ],

      // 大格子列表
      roundCards: [],
      failedRounds: [],
      roundListLoaded: false,
      roundLoadError: '',
      roundLoadTotal: 0,
      loading: false,
      importing: false,
      exporting: false,
      allSaving: false,
      timeSortOrder: 'desc',
      roundLoadSerial: 0,

      // 组别配置
      categories: [],
      categoryLoading: false,
      categoryVisible: false,
      lineupVisible: false,
      lineupTitle: '坐舱名单',
      lineupTeams: [],
      activeLineupTeam: null,
      lineupRound: null,
      lineupEditor: null,
      lineupEditing: false,
      lineupSaving: false,
      lineupCorrectionReason: '',
      lineupSeatRoles: [],
      boatSeatRows: [],
      lineupColumns: [
        { title: '队伍', dataIndex: 'teamName', width: 160 },
        { title: '航道', dataIndex: 'laneNo', width: 70 },
        { title: '编号', dataIndex: 'bibNo', width: 80 },
        { title: '座舱成员', key: 'seatMembers', scopedSlots: { customRender: 'seatMembers' } },
        { title: '替补', key: 'alternates', scopedSlots: { customRender: 'alternates' } }
      ],
      categorySaving: false,
      categoryContentFields: CATEGORY_CONTENT_FIELDS,
      categoryForm: {
        originalContent: {},
        status: 1,
        sortOrder: 0,
        boatTypeCode: 'STANDARD_20',
        substituteMode: 'NONE',
        substituteLimit: 0,
        internationalPublished: 0
      },
      categoryRules: {
        categoryName: [{ required: true, message: '请输入组别名称', trigger: 'blur' }]
      },
      categoryColumns: [
        { title: '组别名称', dataIndex: 'categoryName', width: 180 },
        { title: '船型', dataIndex: 'boatTypeCode', width: 150, customRender: value => this.boatTypeLabel(value) },
        { title: '在船人数', dataIndex: 'onWaterTotal', width: 100 },
        { title: '替补', key: 'substitutes', width: 120, customRender: (value, record) => this.substituteRuleLabel(record) },
        { title: '排序', dataIndex: 'sortOrder', width: 100 },
        { title: '状态', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', width: 160, scopedSlots: { customRender: 'action' } }
      ],
      stageCodeOptions: [
        { value: 'HEAT', label: '預賽 / Heat', zhHans: '预赛', zhHant: '預賽', en: 'Heat' },
        { value: 'REPECHAGE', label: '復活賽 / Repechage', zhHans: '复活赛', zhHant: '復活賽', en: 'Repechage' },
        { value: 'QUARTERFINAL', label: '四分之一決賽 / Quarterfinal', zhHans: '四分之一决赛', zhHant: '四分之一決賽', en: 'Quarterfinal' },
        { value: 'SEMIFINAL', label: '準決賽 / Semifinal', zhHans: '半决赛', zhHant: '準決賽', en: 'Semifinal' },
        { value: 'FINAL', label: '決賽 / Final', zhHans: '决赛', zhHant: '決賽', en: 'Final' },
        { value: 'CLASSIFICATION', label: '排位賽 / Classification', zhHans: '排位赛', zhHant: '排位賽', en: 'Classification race' },
        { value: 'OTHER', label: '其他 / Other', zhHans: '', zhHant: '', en: '' }
      ]
    }
  },
  created() {
    this.activeTabKey = workbenchTab(this.$route.query.tab)
    this.eventId = positiveRouteId(this.$route.query.eventId)
    if (!this.eventId) {
      this.$message.error('未指定赛事ID')
      this.goBack()
      return
    }
    this.loadEventDetail().then(async () => {
      if (!this.viewAlive) return
      await this.loadCategories()
      if (!this.viewAlive) return
      await this.loadTabData(this.activeTabKey)
    }).catch(error => { if (this.viewAlive) this.$message.error(error.message || '加载赛事失败') })
  },
  beforeDestroy() {
    this.viewAlive = false
    this.roundLoadSerial++
  },
  watch: {
    '$route.query.tab'(value) {
      const tab = workbenchTab(value)
      if (!this.viewAlive || tab === this.activeTabKey) return
      this.activeTabKey = tab
      if (this.eventInfo) this.loadTabData(tab).catch(error => { if (this.viewAlive) this.$message.error(error.message || '加载赛事资料失败') })
    }
  },
  methods: {
    resolveResultRoute(selection) {
      if (!this.viewAlive) return
      const query = { ...this.$route.query }
      for (const field of ['categoryId', 'roundId']) {
        if (query[field] != null && !selection[field]) delete query[field]
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {})
    },
    rt(key) { return resultsText(this.$adminLocale, key) },
    onScheduleViewChange() {
      if (this.scheduleView === 'batch') this.loadTabData('results-quick-entry').catch(error => this.$message.error(error.message || '加载编排资料失败'))
    },
    loadTabData(key) {
      if (key === 'stage-config') return this.loadStageConfigs()
      if (key === 'distance-config') return this.loadDistanceConfigs()
      if (key === 'event-entries') return this.loadTeamConfig()
      if (key === 'results-quick-entry' && this.scheduleView === 'batch') return Promise.all([this.loadStageConfigs(), this.loadDistanceConfigs(), this.loadRoundsAndResults()])
      return Promise.resolve()
    },
    // 切换 Tab 时记录到 URL，刷新后保持当前页
    onTabChange(key) {
      this.activeTabKey = workbenchTab(key)
      this.$router.replace({ query: { ...this.$route.query, tab: this.activeTabKey } }).catch(() => {})
      this.loadTabData(this.activeTabKey).catch(error => { if (this.viewAlive) this.$message.error(error.message || '加载赛事资料失败') })
    },
    goBack() {
      this.$router.push('/dragonboat/event')
    },
    loadEventDetail() {
      return getDragonEventDetail({ id: this.eventId }).then(res => {
        if (!this.viewAlive) return
        if (res.data) {
          this.eventInfo = res.data.event
          this.defaultCategoryRule = res.data.defaultCategoryRule || null
          this.eventTitle = this.eventInfo ? this.eventInfo.title : ''
        }
      })
    },
    loadCategories() {
      this.categoryLoading = true
      return getDragonCategoryList({ eventId: this.eventId }).then(res => {
        if (!this.viewAlive) return
        this.categories = (res.data && res.data.list) || []
      }).finally(() => {
        this.categoryLoading = false
      })
    },
    loadStageConfigs() {
      this.stageLoading = true
      return getDragonStageConfigList({ eventId: this.eventId }).then(res => {
        if (!this.viewAlive) return
        this.stageOptions = (res.data && res.data.list) || []
      }).finally(() => {
        this.stageLoading = false
      })
    },
    loadDistanceConfigs() {
      this.distanceLoading = true
      return getDragonDistanceConfigList({ eventId: this.eventId }).then(res => {
        if (!this.viewAlive) return
        this.distanceOptions = (res.data && res.data.list) || []
      }).finally(() => {
        this.distanceLoading = false
      })
    },
    loadTeamConfig() {
      this.teamLoading = true
      return getDragonRegistrationList({
        page: 1,
        pageSize: 500,
        eventId: this.eventId
      }).then(res => {
        if (!this.viewAlive) return
        this.eventTeams = (res.data && res.data.list) || []
      }).finally(() => {
        this.teamLoading = false
      })
    },
    loadTeamOptions() {
      return getDragonTeamList({ page: 1, pageSize: 500, auditStatus: 1 }).then(res => {
        this.teamOptions = (res.data && res.data.list) || []
      })
    },

    loadRoundsAndResults(options = {}) {
      if (!this.viewAlive || this.allSaving && !options.afterSave) return Promise.resolve()
      if ((this.hasRoundEdits || this.roundCards.some(round => round.saveNeedsSync)) && !options.afterSave) {
        this.$message.warning('请先保存页面上的修改，再重新读取整表')
        return Promise.resolve()
      }
      const serial = ++this.roundLoadSerial
      this.loading = true
      this.roundLoadError = ''
      this.roundListLoaded = false
      const filter = { ...this.filter }

      const params = {
        eventId: this.eventId,
        page: 1,
        pageSize: 500,
        categoryId: filter.categoryId
      }
      
      return getDragonRoundList(params, { silentError: true }).then(res => {
        if (!this.viewAlive || serial !== this.roundLoadSerial) return
        const rawRounds = completeRoundList(res)
        // 筛选条件为空时展示总表，有值时按条件缩小范围。
        const filtered = rawRounds.filter(item => {
          const matchStage = !filter.roundName || item.roundName === filter.roundName
          const matchDistance = !filter.distance || parseInt(item.distance) === parseInt(filter.distance)
          const matchStatus = filter.status === undefined || filter.status === null || filter.status === '' || Number(item.status) === Number(filter.status)
          return matchStage && matchDistance && matchStatus
        })
        this.roundListLoaded = true
        this.roundLoadTotal = filtered.length
        this.failedRounds = []
        return this.loadRoundBatch(filtered, serial, true)
      }).catch(error => {
        if (this.viewAlive && serial === this.roundLoadSerial) {
          // A failed refresh is not an empty event. Keep previously loaded cards.
          this.roundLoadError = roundLoadMessage(error)
        }
      }).finally(() => {
        if (this.viewAlive && serial === this.roundLoadSerial) this.loading = false
      })
    },

    loadRoundBatch(rounds, serial, replace = false) {
      const isCurrent = () => this.viewAlive && serial === this.roundLoadSerial
      const loaded = []
      // Per-load cache only: retry and refresh must fetch fresh entry permissions.
      const teamsByCategory = new Map()
      return loadRoundQueue(rounds, async round => {
        this.$set(round, 'results', [])
        this.$set(round, 'participantTeams', [])
        this.$set(round, 'publishing', false)
        this.$set(round, 'roundTimeStr', round.roundTime ? round.roundTime.slice(0, 16).replace('T', ' ') : '')
        // Resolve the registration-to-entry bridge before reading legacy result links.
        await this.loadRoundParticipantTeams(round, teamsByCategory)
        if (!isCurrent()) return
        const res = await getDragonResultList({ roundId: round.id, page: 1, pageSize: 500 }, { silentError: true })
        if (!isCurrent()) return
        const list = completeRoundList(res)
        list.forEach(row => {
          this.$set(row, 'isFoul', row.resultStatus === 'DSQ' || row.remark === '犯规' || row.resultTime === '犯规')
          this.$set(row, 'isDns', row.resultStatus === 'DNS' || row.remark === 'DNS' || row.resultTime === 'DNS')
        })
        list.sort((a, b) => this.compareResultRows(a, b))
        round.results = list
        this.$set(round, 'published', list.some(row => row.published === 1) ? 1 : 0)
        this.$set(round, 'deletedRevisions', {})
        this.$set(round, 'correctionReason', '')
        this.$set(round, 'savedRaceNo', round.raceNo)
        round.savedFingerprint = this.roundFingerprint(round)
      }, (round, error) => {
        this.failedRounds = this.failedRounds.filter(item => item.round.id !== round.id)
        if (error) this.failedRounds.push({ round, message: roundLoadMessage(error) })
        else {
          loaded.push(round)
        }
      }, isCurrent).then(() => {
        if (!isCurrent()) return
        // Publish one complete snapshot, never a table that grows under the cursor.
        if (replace && this.failedRounds.length) return
        this.roundCards = replace ? loaded : this.roundCards.filter(item => !loaded.some(next => next.id === item.id)).concat(loaded)
        this.sortRoundCardsByTime(this.roundCards, this.timeSortOrder)
      })
    },

    retryFailedRounds() {
      if (!this.viewAlive || this.loading || this.allSaving) return Promise.resolve()
      return this.loadRoundsAndResults()
    },

    handleFilterChange() {
      // Apply all selected filters together when the user clicks Query.
    },

    compareResultRows(a, b) {
      const rankA = this.hasEffectiveRank(a.rankNo)
      const rankB = this.hasEffectiveRank(b.rankNo)
      if (rankA || rankB) {
        if (rankA !== rankB) {
          return rankA ? -1 : 1
        }
        const rankNoA = Number(a.rankNo)
        const rankNoB = Number(b.rankNo)
        if (rankNoA !== rankNoB) {
          return rankNoA - rankNoB
        }
      }
      const laneA = a.laneNo || 999999
      const laneB = b.laneNo || 999999
      if (laneA !== laneB) {
        return laneA - laneB
      }
      const sortA = a.sortOrder || 999999
      const sortB = b.sortOrder || 999999
      if (sortA !== sortB) {
        return sortA - sortB
      }
      return (a.id || 0) - (b.id || 0)
    },

    hasEffectiveRank(rankNo) {
      const rank = Number(rankNo)
      return Number.isFinite(rank) && rank > 0 && rank < 99
    },

    sortRoundResults(round) {
      if (!round || !round.results) return
      round.results.sort((a, b) => this.compareResultRows(a, b))
      round.results.forEach((row, index) => {
        this.$set(row, 'sortOrder', index + 1)
      })
    },

    sortRoundCardsByTime(list, order) {
      list.sort((a, b) => {
        const timeA = a.roundTimeStr || (a.roundTime ? String(a.roundTime).slice(0, 16).replace('T', ' ') : '')
        const timeB = b.roundTimeStr || (b.roundTime ? String(b.roundTime).slice(0, 16).replace('T', ' ') : '')
        if (timeA !== timeB) {
          return order === 'asc' ? String(timeA).localeCompare(String(timeB)) : String(timeB).localeCompare(String(timeA))
        }
        const orderA = a.roundOrder || 0
        const orderB = b.roundOrder || 0
        return order === 'asc' ? orderA - orderB : orderB - orderA
      })
    },

    handleSortRoundsByTime() {
      const order = this.timeSortOrder === 'asc' ? 'desc' : 'asc'
      this.timeSortOrder = order
      this.sortRoundCardsByTime(this.roundCards, order)
      this.$message.success(order === 'asc' ? '已按比赛时间正序排列' : '已按比赛时间倒序排列')
    },

    downloadWorkbookTemplate() {
      request({ url: '/dragonboat/result/workbook-template', responseType: 'blob' }).then(res => this.downloadResultWorkbook(res, '赛程成绩模板.xlsx')).catch(() => {})
    },

    downloadResultWorkbook(data, filename) {
      const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    },

    handleExportResults() {
      if (this.exporting) return
      this.exporting = true
      exportDragonResults({ eventId: this.eventId }).then(res => {
        this.downloadResultWorkbook(res, `赛程成绩_${new Date().toISOString().slice(0, 10)}.xlsx`)
        this.$message.success('导出成功')
      }).catch(() => {}).finally(() => { this.exporting = false })
    },

    handleWorkbookImport({ file, onSuccess, onError }) {
      if (this.roundDataIncomplete || this.allSaving) {
        onError(new Error('请等待赛程完整加载后再导入'))
        return
      }
      if (this.importing) {
        onError(new Error('正在导入中'))
        return
      }
      if (this.roundCards.some(round => round.savedFingerprint !== this.roundFingerprint(round))) {
        this.$message.warning('请先保存页面上的修改，再导入文件')
        onError(new Error('页面有未保存修改'))
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      this.importing = true
      importDragonResultWorkbook(this.eventId, formData).then(res => {
        const data = res.data || {}
        const updated = Number(data.overwrittenRoundCount || 0)
        this.$message.success(`已导入 ${data.roundCount || 0} 场、${data.resultCount || 0} 条赛程成绩草稿${updated ? `（其中更新未发布草稿 ${updated} 场，以文件为准）` : ''}。核对后点击“保存全部”同步有效成绩。`)
        this.loadTeamConfig()
        this.loadTeamOptions()
        this.loadCategories()
        this.loadStageConfigs()
        this.loadDistanceConfigs()
        this.loadRoundsAndResults()
        onSuccess(res)
      }).catch(err => {
        onError(err)
      }).finally(() => {
        this.importing = false
      })
    },

    loadRoundParticipantTeams(round, cache) {
      if (!round.categoryId) {
        this.$set(round, 'participantTeams', [])
        return Promise.resolve()
      }
      const key = String(round.categoryId)
      let pending = cache && cache.get(key)
      if (!pending) {
        pending = request({ url: '/dragonboat/event-entry/list', params: { eventId: this.eventId, categoryId: round.categoryId }, silentError: !!cache }).then(completeRoundList)
        if (cache) cache.set(key, pending)
      }
      return pending.then(list => {
        this.$set(round, 'participantTeams', list)
      })
    },

    // 航道内队伍选择改变，自动回填 teamName
    handleRowTeamChange(row, eventEntryId, round) {
      const team = ((round && round.participantTeams) || []).find(t => t.id === eventEntryId)
      if (team) {
        row.teamName = team.teamName
        row.teamId = team.teamId || null
      }
    },

    handleRoundCategoryChange(round) {
      this.loadRoundParticipantTeams(round)
      round.results.forEach(row => {
        row.categoryId = round.categoryId
        row.eventEntryId = undefined
        row.teamId = undefined
        row.teamName = ''
      })
    },

    handleRoundStageChange(round) {
      const stage = this.activeStageOptions.find(item => item.stageName === round.roundName)
      if (!stage) return
      this.$set(round, 'stageCode', stage.stageCode || 'OTHER')
      this.$set(round, 'roundNameZhHant', stage.stageNameZhHant || '')
      this.$set(round, 'roundNameEn', stage.stageNameEn || '')
    },

    // 勾选犯规/DNS
    handleRowStatusChange(row, type, round) {
      if (type === 'foul' && row.isFoul) {
        row.resultStatus = 'DSQ'
        row.isDns = false
        row.resultTime = '犯规'
        row.remark = '犯规'
        row.rankNo = 99
        row.gapTime = '--'
      } else if (type === 'dns' && row.isDns) {
        row.resultStatus = 'DNS'
        row.isFoul = false
        row.resultTime = 'DNS'
        row.remark = 'DNS'
        row.rankNo = 99
        row.gapTime = '--'
      } else {
        row.resultStatus = 'FINISHED'
        row.resultTime = ''
        row.remark = ''
        row.rankNo = undefined
        row.gapTime = ''
      }
      if (round) {
        this.handleRecalculateRound(round, false)
      }
    },

    // 添加队伍成绩行
    handleAddNewResultRow(round) {
      if (!round.categoryId) {
        this.$message.warning('请先选择组别')
        return
      }
      round.results.push({
        tempId: `row_${Date.now()}_${round.results.length}`,
        eventId: parseInt(this.eventId),
        categoryId: round.categoryId,
        roundId: round.id,
        laneNo: round.results.length + 1,
        eventEntryId: undefined,
        teamId: undefined,
        teamName: '',
        bibNo: '',
        resultTime: '',
        rankNo: undefined,
        gapTime: '',
        published: 0,
        remark: '',
        isFoul: false,
        isDns: false
      })
    },

    // 移除行
    handleRemoveRow(round, idx) {
      const removed = round.results[idx]
      if (removed.published === 1) return
      if (removed.id) {
        if (!round.deletedRevisions) this.$set(round, 'deletedRevisions', {})
        this.$set(round.deletedRevisions, removed.id, removed.resultRevision)
      }
      round.results.splice(idx, 1)
      // 重新排航道号
      round.results.forEach((row, i) => {
        row.laneNo = i + 1
      })
    },

    handleResultDragEnd(round) {
      round.results.forEach((row, index) => {
        this.$set(row, 'sortOrder', index + 1)
      })
    },

    handleRecalculateRound(round, showMessage = true) {
      const results = round.results
      if (results.length === 0) return

      // 1. 将有效的成绩解析成秒数进行排序
      const tempRows = results.map((row, idx) => {
        let seconds = Infinity
        const isInvalid = row.isFoul || row.isDns || !row.resultTime || row.resultTime === '犯规' || row.resultTime === 'DNS'
        if (!isInvalid) {
          seconds = this.parseTimeToSeconds(row.resultTime)
        }
        return { idx, seconds, isInvalid, rawTime: row.resultTime }
      })

      // 找出最快完赛秒数
      let minSeconds = Infinity
      tempRows.forEach(item => {
        if (!item.isInvalid && item.seconds < minSeconds) {
          minSeconds = item.seconds
        }
      })

      // 2. 排序有效完赛的队伍计算名次
      const validFinished = tempRows
        .filter(item => !item.isInvalid && item.seconds !== Infinity)
        .sort((a, b) => a.seconds - b.seconds)

      // 设定名次 (处理并列名次的情况)
      let currentRank = 1
      validFinished.forEach((item, i) => {
        if (i > 0 && item.seconds > validFinished[i - 1].seconds) {
          currentRank = i + 1
        }
        results[item.idx].rankNo = currentRank
      })

      // 3. 计算每一行差值和回填
      results.forEach((row, index) => {
        const tInfo = tempRows.find(t => t.idx === index)
        if (row.isFoul || row.isDns) {
          row.rankNo = 99
          row.gapTime = '--'
        } else if (tInfo.seconds === Infinity) {
          row.rankNo = undefined
          row.gapTime = '--'
        } else {
          // 第一名显示自身成绩，其他显示差值时间
          if (tInfo.seconds === minSeconds) {
            row.gapTime = row.resultTime
          } else {
            const diff = tInfo.seconds - minSeconds
            row.gapTime = `+${diff.toFixed(2)}`
          }
        }
      })
      this.sortRoundResults(round)

      if (showMessage) {
        this.$message.success('计算完成')
      }
    },

    // 辅助解析时间字符为秒
    parseTimeToSeconds(timeStr) {
      if (!timeStr) return Infinity
      const str = String(timeStr).trim()
      if (!str) return Infinity
      if (str.includes(':')) {
        const parts = str.split(':')
        const min = parseFloat(parts[0]) || 0
        const sec = parseFloat(parts[1]) || 0
        return min * 60 + sec
      }
      return parseFloat(str) || Infinity
    },

    // 渲染统计：本组最快成绩
    getRoundFastestTime(round) {
      let fastest = '-'
      let minSec = Infinity
      round.results.forEach(row => {
        const isInvalid = row.isFoul || row.isDns || !row.resultTime || row.resultTime === '犯规' || row.resultTime === 'DNS'
        if (!isInvalid) {
          const sec = this.parseTimeToSeconds(row.resultTime)
          if (sec < minSec) {
            minSec = sec
            fastest = row.resultTime
          }
        }
      })
      return fastest
    },

    validateRoundBeforeSave(round, index) {
      const label = round.raceNo ? `Race ${round.raceNo}` : `第 ${index + 1} 个比赛组`
      if (!round.categoryId) {
        this.$message.warning(`${label}：请选择组别`)
        return false
      }
      if (!round.roundName) {
        this.$message.warning(`${label}：请选择阶段`)
        return false
      }
      if (round.results.some(row => !row.eventEntryId)) {
        this.$message.warning(`${label}：请从本赛事参赛队伍清单选择队伍`)
        return false
      }
      if (!round.distance) {
        this.$message.warning(`${label}：请选择距离`)
        return false
      }
      if (!round.roundTimeStr) {
        this.$message.warning(`${label}：请输入比赛时间`)
        return false
      }
      return true
    },

    buildRoundSavePayload(round) {
      this.sortRoundResults(round)

      round.roundTime = `${round.roundTimeStr}:00`

      const nextOrder = round.roundOrder || this.getNextRoundOrder()
      const roundPayload = {
        id: round.id,
        eventId: round.eventId || parseInt(this.eventId),
        categoryId: round.categoryId,
        roundName: round.roundName,
        roundCode: round.roundCode,
        stageCode: round.stageCode,
        raceNo: round.savedRaceNo == null || round.raceNo !== round.savedRaceNo ? round.raceNo : undefined,
        heatNo: round.heatNo || 1,
        roundNameZhHant: round.roundNameZhHant,
        roundNameEn: round.roundNameEn,
        roundOrder: nextOrder,
        roundTime: round.roundTime,
        status: round.status == null ? 0 : round.status,
        distance: round.distance,
        description: round.description
      }

      const buildBatchPayload = savedRound => ({
        roundId: savedRound.id,
        syncResults: true,
        correctionReason: round.correctionReason || null,
        deletedRevisions: round.deletedRevisions || {},
          results: round.results.map((row, index) => ({
            id: row.id,
            eventEntryId: row.eventEntryId,
            resultRevision: row.resultRevision,
            eventId: row.eventId || parseInt(this.eventId),
            categoryId: round.categoryId,
            roundId: savedRound.id,
            laneNo: row.laneNo,
            teamId: row.teamId,
            teamName: row.teamName,
            bibNo: row.bibNo,
            resultTime: row.isFoul || row.isDns ? '' : row.resultTime,
            resultStatus: row.isFoul ? 'DSQ' : row.isDns ? 'DNS' : row.resultStatus || 'FINISHED',
            rankNo: row.isFoul || row.isDns ? 0 : row.rankNo,
            sortOrder: index + 1,
            gapTime: row.gapTime,
            published: 0,
            remark: row.remark
        }))
      })

      return { nextOrder, roundPayload, buildBatchPayload }
    },

    saveRoundWithResults(round) {
      if (this.viewAlive === false) return Promise.reject(new Error('工作台已离开，已停止后续保存'))
      if (this.roundDataIncomplete) return Promise.reject(new Error('赛程尚未完整加载，请重试成功后再保存'))
      if (round.published === 1 && !String(round.correctionReason || '').trim()) return Promise.reject(new Error('修改公开成绩请填写本卡片的更正原因'))
      const payload = this.buildRoundSavePayload(round)
      return saveDragonRound(payload.roundPayload).then(res => {
        // An in-flight write may complete, but leaving this event must stop the
        // following request and subsequent races in the old batch-save chain.
        if (this.viewAlive === false) throw new Error('工作台已离开，已停止后续保存')
        const savedRound = res.data || {}
        round.id = savedRound.id || round.id
        round.roundOrder = savedRound.roundOrder || payload.nextOrder
        round.raceNo = savedRound.raceNo || round.raceNo
        round.stageCode = savedRound.stageCode || round.stageCode
        round.heatNo = savedRound.heatNo || round.heatNo

        return batchSaveDragonResults(payload.buildBatchPayload(round)).then(() => {
          this.$set(round, 'saveNeedsSync', true)
          return this.syncSavedRound(round)
        })
      })
    },

    syncSavedRound(round, readFailureMessage) {
      return getDragonResultList({ roundId: round.id, page: 1, pageSize: 500 }, { silentError: true }).then(res => {
        const rows = completeRoundList(res)
        if (!this.viewAlive) return
        rows.forEach(row => {
          this.$set(row, 'isFoul', row.resultStatus === 'DSQ' || row.remark === '犯规' || row.resultTime === '犯规')
          this.$set(row, 'isDns', row.resultStatus === 'DNS' || row.remark === 'DNS' || row.resultTime === 'DNS')
        })
        rows.sort((a, b) => this.compareResultRows(a, b))
        round.results = rows
        round.published = rows.some(row => row.published === 1) ? 1 : 0
        round.deletedRevisions = {}
        round.correctionReason = ''
        round.savedRaceNo = round.raceNo
        round.saveNeedsSync = false
        round.savedFingerprint = this.roundFingerprint(round)
      }).catch(() => {
        throw new Error(readFailureMessage || '该场次已保存，但回读确认失败。输入已保留，请再次点击保存，仅重试确认，不重复提交该场次。')
      })
    },

    categoryNameOf(categoryId) {
      const found = (this.categories || []).find(item => item.id === categoryId)
      return found ? found.categoryName : ''
    },

    teamLabel(round, row) {
      const team = (round.participantTeams || []).find(item => item.id === row.eventEntryId)
      const name = (team && team.teamName) || row.teamName || ''
      const code = (team && team.entryCode) || ''
      if (!name) return row.eventEntryId == null ? '未安排参赛队' : `参赛队 #${row.eventEntryId}`
      return code ? `${name} · ${code}` : name
    },

    handleWithdrawPublish(round) {
      if (this.viewAlive === false || this.roundDataIncomplete || this.allSaving || this.importing) return Promise.resolve()
      if (round.published !== 1) return Promise.resolve()
      if (this.hasRoundEdits) {
        this.$message.warning('请先保存页面上的修改，再撤回发布')
        return Promise.resolve()
      }
      const reason = String(round.correctionReason || '').trim()
      if (!reason) {
        this.$message.warning('撤回发布请先在本卡片填写更正原因')
        return Promise.resolve()
      }
      const expectedRevisions = {}
      round.results.forEach(row => { if (row.id) expectedRevisions[row.id] = row.resultRevision })
      if (!Object.keys(expectedRevisions).length) {
        this.$message.warning('当前场次没有可撤回的已发布成绩')
        return Promise.resolve()
      }
      this.allSaving = true
      return publishDragonResult({ eventId: Number(this.eventId), categoryId: round.categoryId, roundId: round.id, published: 0, correctionReason: reason, expectedRevisions })
        .then(() => this.syncSavedRound(round, '已撤回发布，但回读确认失败。请点击查询重新读取整表核对'))
        .then(() => { if (this.viewAlive) this.$message.success('已撤回发布：可修改队伍或成绩，保存全部后重新发布') })
        .catch(error => { if (this.viewAlive) this.$message.error(error.message || '撤回发布失败') })
        .finally(() => { if (this.viewAlive) this.allSaving = false })
    },

    roundFingerprint(round) {
      const metadata = ['categoryId','roundName','stageCode','raceNo','heatNo','roundTimeStr','status','distance','description'].map(key => round[key] == null ? null : round[key])
      const rows = (round.results || []).map(row => ['id','eventEntryId','teamId','teamName','registrationId','laneNo','bibNo','resultTime','resultStatus','rankNo','gapTime','remark','isFoul','isDns'].map(key => row[key] == null ? null : row[key]))
      return JSON.stringify([metadata,rows,round.deletedRevisions || {}])
    },
    // 统一保存当前录入页全部比赛组
    handleSaveAllRounds() {
      if (this.allSaving || this.importing || this.roundDataIncomplete) {
        this.$message.warning('请等待操作完成，并重试所有加载失败的场次后再保存')
        return Promise.resolve()
      }
      const pending = this.roundCards.filter(round => round.saveNeedsSync)
      if (pending.length) {
        this.allSaving = true
        return pending.reduce((promise, round) => promise.then(() => this.syncSavedRound(round)), Promise.resolve())
          .then(() => { if (this.viewAlive) this.$message.success('已确认先前保存结果；其他未保存修改请继续点击保存全部') })
          .catch(err => { if (this.viewAlive) this.$message.error(err.message) })
          .finally(() => { this.allSaving = false })
      }
      const editableRounds = this.roundCards.filter(round => !round.id || round.savedFingerprint !== this.roundFingerprint(round) || round.results.some(row => row.published !== 1 && (row.resultTime || row.rankNo > 0 || row.isFoul || row.isDns || row.resultStatus === 'DNF')))
      if (!editableRounds.length) {
        this.$message.warning('当前没有未保存的修改')
        return
      }
      const invalidIndex = editableRounds.findIndex((round, index) => !this.validateRoundBeforeSave(round, index))
      if (invalidIndex !== -1) {
        return
      }
      this.allSaving = true
      return editableRounds.reduce((promise, round) => {
        return promise.then(() => this.saveRoundWithResults(round)).then(() => {
          round.savedFingerprint = this.roundFingerprint(round)
        })
      }, Promise.resolve()).then(() => {
        if (!this.viewAlive) return
        this.$message.success('已保存全部修改：赛程已保留，有效成绩已同步')
      }).catch(err => {
        if (this.viewAlive) this.$message.error(err.message || '保存失败')
      }).finally(() => {
        this.allSaving = false
      })
    },

    // 添加一个 Round 大格子
    handleAddNewRound() {
      if (this.roundDataIncomplete || this.allSaving) return
      const round = {
        tempId: `temp_${Date.now()}`,
        eventId: parseInt(this.eventId),
        categoryId: undefined,
        roundName: undefined,
        stageCode: 'HEAT',
        raceNo: undefined,
        heatNo: 1,
        roundOrder: this.getNextRoundOrder(),
        roundTimeStr: this.getDefaultRoundDateTime(),
        status: 0,
        distance: undefined,
        description: '',
        results: [],
        participantTeams: []
      }
      this.roundCards.unshift(round)
    },

    handleDeleteRound(round) {
      if (this.roundDataIncomplete || this.allSaving) return
      if (!round.id) {
        this.roundCards = this.roundCards.filter(item => item !== round)
        return
      }
      return deleteDragonRound({ id: round.id }).then(() => {
        if (!this.viewAlive) return
        this.roundCards = this.roundCards.filter(item => item !== round)
        this.$message.success(`Race ${round.raceNo || round.roundOrder} 已删除`)
        // Do not refresh away edits in other cards after a successful deletion.
        if (!this.hasRoundEdits) return this.loadRoundsAndResults()
      }).catch(err => {
        this.$message.error(err.message || '删除本组失败')
      })
    },

    getNextRoundOrder() {
      if (!this.roundCards.length) {
        return 1
      }
      return Math.max(...this.roundCards.map(item => item.roundOrder || 0)) + 1
    },

    getDefaultRoundDateTime() {
      const latest = this.roundCards
        .map(item => item.roundTimeStr || (item.roundTime ? String(item.roundTime).slice(0, 16).replace('T', ' ') : ''))
        .filter(Boolean)
        .sort()
        .pop()
      if (latest) {
        return latest
      }
      return `${this.getEventStartDate()} 09:00`
    },

    calculateNextTime(lastTime) {
      if (!lastTime || !lastTime.includes(':')) return '09:00'
      const parts = lastTime.split(':')
      let hour = parseInt(parts[0]) || 9
      let min = parseInt(parts[1]) || 0
      min += 8
      if (min >= 60) {
        hour += Math.floor(min / 60)
        min = min % 60
      }
      if (hour >= 24) {
        hour = hour % 24
      }
      return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
    },

    // ==================== 组别管理部分 ====================
    handleAddCategory() {
      this.categoryForm = createContentDraft({
        eventId: parseInt(this.eventId),
        status: 1,
        sortOrder: 0,
        categoryName: '',
        boatTypeCode: 'STANDARD_20',
        substituteMode: 'NONE',
        substituteLimit: 0,
        internationalPublished: 0
      }, CATEGORY_CONTENT_FIELDS)
      this.categoryAdvanced = false
      if (this.acceptsRegistration && this.defaultCategoryRule) {
        ['boatTypeCode', 'paddlerRows', 'paddlersPerRow', 'paddlerCount', 'drummerCount', 'steererCount', 'onWaterTotal', 'substituteMode', 'substituteLimit'].forEach(key => this.$set(this.categoryForm, key, this.defaultCategoryRule[key]))
      }
      else { this.applyBuiltInBoatRule('STANDARD_20'); this.categoryAdvanced = true }
      this.categoryVisible = true
    },
    handleEditCategory(record) {
      this.categoryAdvanced = false
      this.categoryForm = createContentDraft(record, CATEGORY_CONTENT_FIELDS)
      this.categoryVisible = true
    },
    handleCategoryBoatTypeChange(value) {
      if (value === 'STANDARD_20' || value === 'SMALL_10') {
        this.applyBuiltInBoatRule(value)
      } else {
        this.$set(this.categoryForm, 'paddlerRows', this.categoryForm.paddlerRows || 1)
        this.$set(this.categoryForm, 'paddlersPerRow', this.categoryForm.paddlersPerRow || 2)
        this.$set(this.categoryForm, 'drummerCount', this.categoryForm.drummerCount == null ? 0 : this.categoryForm.drummerCount)
        this.$set(this.categoryForm, 'steererCount', this.categoryForm.steererCount == null ? 0 : this.categoryForm.steererCount)
      }
    },
    applyBuiltInBoatRule(value) {
      const standard = value === 'STANDARD_20'
      this.$set(this.categoryForm, 'paddlerRows', standard ? 10 : 5)
      this.$set(this.categoryForm, 'paddlersPerRow', 2)
      this.$set(this.categoryForm, 'paddlerCount', standard ? 20 : 10)
      this.$set(this.categoryForm, 'drummerCount', 1)
      this.$set(this.categoryForm, 'steererCount', 1)
      this.$set(this.categoryForm, 'onWaterTotal', standard ? 22 : 12)
    },
    boatTypeLabel(value) {
      if (value === 'STANDARD_20') return '大龍 / Standard'
      if (value === 'SMALL_10') return '小龍 / Small'
      return '自定義 / Custom'
    },
    categoryCrewSummary(category) {
      if (!category) return ''
      if (category.boatTypeCode === 'STANDARD_20') return '10排20名划手，鼓手及舵手各1名；在船22人'
      if (category.boatTypeCode === 'SMALL_10') return '5排10名划手，鼓手及舵手各1名；在船12人'
      const paddlers = (Number(category.paddlerRows) || 0) * (Number(category.paddlersPerRow) || 0)
      return `${paddlers}名划手，鼓手${Number(category.drummerCount) || 0}名、舵手${Number(category.steererCount) || 0}名；在船共${paddlers + (Number(category.drummerCount) || 0) + (Number(category.steererCount) || 0)}人`
    },
    substituteRuleLabel(category) {
      if (!category || category.substituteMode === 'NONE') return '不設替補'
      if (category.substituteMode === 'UNLIMITED') return '不限'
      return `最多${category.substituteLimit || 0}人`
    },
    openRoundLineup(round) {
      this.lineupRound = round
      this.lineupEditor = null
      this.lineupTeams = []
      this.activeLineupTeam = null
      this.lineupTitle = `坐舱名单 · ${round.roundName || '比赛组'}${round.distance ? '（' + round.distance + '米）' : ''}`
      this.lineupVisible = true
      getDragonRegistrationRoundLineups({
        eventId: this.eventId,
        roundId: round.id,
        categoryId: round.categoryId
      }).then(res => {
        this.lineupTeams = res.data.teams || []
        this.boatSeatRows = res.data.boatSeatRows || []
        this.lineupSeatRoles = res.data.seatRoles || []
        this.activeLineupTeam = this.lineupTeams[0] || null
        if (res.data.roundName) {
          this.lineupTitle = `坐舱名单 · ${res.data.roundName}`
        }
      })
    },
    async editRoundSeats() {
      this.lineupCorrectionReason = ''
      try {
        const res = await getDragonAdminRoundLineup({ registrationId: this.activeLineupTeam.registrationId, roundId: this.lineupRound.id })
        const data = res.data
        this.lineupEditor = { ...data, seats: Array.from({ length: data.onWaterTotal }, (_, i) => ({ memberId: (data.seatMembers.find(m => m.seatOrder === i + 1) || {}).memberId })), alternates: data.alternateMembers.map(m => m.memberId) }
        this.lineupEditing = true
      } catch (e) { this.$message.error(e.message || '加载名单失败') }
    },
    async saveRoundSeats(confirm) {
      if (this.lineupSaving) return
      if (!this.lineupCorrectionReason.trim()) return this.$message.warning('请填写名单更正原因')
      this.lineupSaving = true
      try {
        const data = this.lineupEditor
        await saveDragonAdminRoundLineup({ registrationId: data.registrationId, roundId: data.roundId, revision: data.revision, confirm, correctionReason: this.lineupCorrectionReason.trim(),
          seatAssignments: data.seats.map((s,i) => ({ memberId: s.memberId, seatOrder: i + 1 })).filter(s => s.memberId), alternateMemberIds: data.alternates })
        this.lineupEditing = false
        this.$message.success(confirm ? '完整名单已确认' : '名单草稿已保存')
        this.openRoundLineup(this.lineupRound)
      } catch (e) { this.$message.error(e.message || '保存失败，请刷新名单后重试') }
      finally { this.lineupSaving = false }
    },
    handleSaveCategory() {
      if (this.categorySaving) return
      this.$refs.categoryFormRef.validate(async valid => {
        if (!valid) return
        this.categorySaving = true
        try {
          await this.$refs.categoryContent.prepare()
          await saveDragonCategory(contentPayload(this.categoryForm))
          this.$message.success('组别保存成功')
          this.categoryVisible = false
          this.loadCategories()
        } catch (err) {
          this.$message.error(err.message || '保存组别失败')
        } finally { this.categorySaving = false }
      })
    },
    handleDeleteCategory(id) {
      deleteDragonCategory({ id }).then(() => {
        this.$message.success('组别删除成功')
        this.loadCategories()
        if (this.scheduleView === 'batch') this.loadRoundsAndResults()
      }).catch(err => {
        this.$message.error(err.message || '组别已有报名或成绩数据，不可删除')
      })
    },
    getCategoryName(categoryId) {
      const item = this.categories.find(c => c.id === categoryId)
      return item ? item.categoryName : '未分组'
    },
    getRoundTitle(round) {
      const parts = []
      if (round.categoryId) {
        parts.push(this.getCategoryName(round.categoryId))
      }
      if (round.distance) {
        parts.push(`${round.distance}米`)
      }
      if (round.roundName) {
        parts.push(round.roundName)
      }
      return parts.length ? parts.join(' ') : '请选择组别、阶段和距离'
    },
    getEventStartDate() {
      if (this.eventInfo && this.eventInfo.eventStartDate) {
        return this.eventInfo.eventStartDate
      }
      if (this.eventInfo && this.eventInfo.eventTime) {
        return this.eventInfo.eventTime.slice(0, 10)
      }
      return new Date().toISOString().slice(0, 10)
    },
    async handleAddEventTeam() {
      try { await Promise.all([this.loadTeamConfig(), this.loadTeamOptions()]) } catch (error) { this.$message.error(error.message || '加载队伍失败'); return }
      this.teamForm = {
        eventId: parseInt(this.eventId),
        categoryId: this.filter.categoryId,
        teamId: undefined,
        memberIds: [],
        bibNo: ''
      }
      this.teamVisible = true
    },
    async loadRegistrationMemberChoices(teamId) {
      this.$set(this.teamForm, 'memberIds', [])
      this.registrationMemberChoices = []
      this.registrationMembersLoading = true
      try {
        const res = await getDragonMemberList({ teamId, page: 1, pageSize: 500, joinStatus: 1 })
        if (this.teamForm.teamId === teamId) this.registrationMemberChoices = res.data.list || []
      } catch (e) { this.$message.error(e.message || '加载人员失败') }
      finally { this.registrationMembersLoading = false }
    },
    handleSaveEventTeam() {
      if (!this.teamForm.categoryId || !this.teamForm.teamId) {
        this.$message.warning('请选择组别和队伍')
        return
      }
      if (!(this.teamForm.memberIds || []).length) { this.$message.warning('请勾选本次报名人员'); return }
      addDragonRegistrationTeam(this.teamForm).then(() => {
        this.$message.success('队伍已加入赛事')
        this.teamVisible = false
        this.registrationReloadKey++
        this.loadTeamConfig()
        this.loadRoundsAndResults()
      }).catch(err => {
        this.$message.error(err.message || '加入队伍失败')
      })
    },
    handleReviewEventTeam(record, auditStatus) {
      auditDragonRegistration({
        registrationId: record.id,
        auditStatus,
        auditRemark: auditStatus === 1 ? '后台审核通过' : '后台审核拒绝',
        bibNo: record.bibNo
      }).then(() => {
        this.$message.success('队伍状态已更新')
        this.loadTeamConfig()
        this.loadRoundsAndResults()
      })
    },
    handleDeleteEventTeam(record) {
      deleteDragonRegistration({ registrationId: record.id }).then(() => {
        this.$message.success('队伍已从本赛事删除')
        this.loadTeamConfig()
        this.loadRoundsAndResults()
      }).catch(err => {
        this.$message.error(err.message || '删除队伍失败')
      })
    },
    registrationStatusColor(status) {
      if (status === 1) return 'green'
      if (status === 2) return 'red'
      return 'orange'
    },
    registrationStatusText(status) {
      const labels = { 0: '待审核', 1: '已通过', 2: '已拒绝' }
      return labels[status] || '未知'
    },
    handleAddStage() {
      this.stageForm = createContentDraft({
        eventId: parseInt(this.eventId),
        stageName: '',
        stageCode: 'HEAT',
        stageOrder: this.stageOptions.length + 1,
        status: 1
      }, STAGE_CONTENT_FIELDS)
      this.applyStageCodeNames('HEAT')
      this.stageVisible = true
    },
    handleEditStage(record) {
      this.stageForm = createContentDraft(record, STAGE_CONTENT_FIELDS)
      this.stageVisible = true
    },
    async handleSaveStage() {
      if (this.stageSaving) return
      this.stageSaving = true
      try {
        await this.$refs.stageContent.prepare()
        await saveDragonStageConfig({ ...contentPayload(this.stageForm), stageCode: this.stageForm.id ? this.stageForm.stageCode : null })
        this.$message.success(this.$t('stageSaved'))
        this.stageVisible = false
        await this.loadStageConfigs()
        this.loadRoundsAndResults()
      } catch (err) {
        this.$message.error(err.message || '保存阶段失败')
      } finally { this.stageSaving = false }
    },
    stageTypeLabel(code) {
      const preset = this.stageCodeOptions.find(item => item.value === code)
      if (!preset || code === 'OTHER') return this.$t('stageOther')
      return preset[this.$adminLocale === 'en' ? 'en' : this.$adminLocale === 'zh-Hant' ? 'zhHant' : 'zhHans']
    },
    applyStageCodeNames(code) {
      const preset = this.stageCodeOptions.find(item => item.value === code)
      if (!preset || code === 'OTHER') return
      const source = this.stageForm.originalContent.stageName
      // Presets help with standard names; changing type must not erase a custom name.
      if (!source || this.stageCodeOptions.some(item => [item.zhHans, item.zhHant, item.en].includes(source))) {
        this.$set(this.stageForm.originalContent, 'stageName', this.stageTypeLabel(code))
      }
    },
    handleDeleteStage(id) {
      deleteDragonStageConfig({ id }).then(() => {
        this.$message.success('阶段删除成功')
        this.loadStageConfigs().then(() => {
          this.loadRoundsAndResults()
        })
      })
    },
    handleAddDistance() {
      this.distanceForm = {
        eventId: parseInt(this.eventId),
        distance: undefined,
        distanceOrder: this.distanceOptions.length + 1,
        status: 1
      }
      this.distanceVisible = true
    },
    handleEditDistance(record) {
      this.distanceForm = { ...record }
      this.distanceVisible = true
    },
    handleSaveDistance() {
      if (!this.distanceForm.distance) {
        this.$message.warning('请输入比赛距离')
        return
      }
      saveDragonDistanceConfig(this.distanceForm).then(() => {
        this.$message.success('距离保存成功')
        this.distanceVisible = false
        this.loadDistanceConfigs().then(() => {
          this.loadRoundsAndResults()
        })
      }).catch(err => {
        this.$message.error(err.message || '保存距离失败')
      })
    },
    handleDeleteDistance(id) {
      deleteDragonDistanceConfig({ id }).then(() => {
        this.$message.success('距离删除成功')
        this.loadDistanceConfigs().then(() => {
          this.loadRoundsAndResults()
        })
      })
    },
    statusColor(status) {
      const colors = { 0: 'orange', 1: 'blue', 2: 'green', 3: 'default', 4: 'gold' }
      return colors[status] || 'default'
    },
    statusText(status) {
      const labels = { 0: '准备阶段', 1: '报名阶段', 2: '比赛阶段', 3: '已结束', 4: '报名截止，待开赛' }
      return labels[status] || '未知'
    },
    rankBadgeClass(rankNo) {
      if (rankNo === 1) return 'rank-badge-1'
      return ''
    },
    formatRankNo(rankNo, isInvalid) {
      if (isInvalid) return '--'
      return rankNo || '-'
    },
    gapTimeClass(rankNo, isInvalid) {
      if (isInvalid) return 'gap-time-label'
      if (rankNo === 1) return 'gap-time-label gap-best'
      return 'gap-time-label gap-other'
    },
    formatGapText(gapTime, isInvalid) {
      if (isInvalid) return '--'
      return gapTime || '-'
    }
  }
}
</script>

<style scoped>
.unified-panel { padding: 20px 24px 0; }
.unified-note { color: var(--jk-text-secondary, #64748b); line-height: 1.7; margin: 0 0 16px; }
.unified-switch { margin-bottom: 18px; }
@media (max-width: 640px) {
  .unified-panel { padding: 16px 16px 0; }
  .unified-switch { display: flex; max-width: 100%; }
  .unified-switch .ant-radio-button-wrapper { height: auto; min-height: 38px; white-space: normal; text-align: center; flex: 1; line-height: 1.4; padding: 9px 12px; }
}
.workbench-header {
  background: #ffffff;
  padding: 20px 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border: 1px solid #e8eaf0;
}

.workbench-content {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  border: 1px solid #e8eaf0;
  overflow: hidden;
}

/* 筛选项样式 */
.filter-bar {
  padding: 24px;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  display: block;
}

/* 按钮样式扩展 */
.btn-success {
  background-color: #10b981;
  border-color: #10b981;
}
.btn-success:hover, .btn-success:focus {
  background-color: #059669;
  border-color: #059669;
}

.table-action-button {
  margin-right: 2px;
}

.table-action-danger,
.table-action-danger:hover,
.table-action-danger:focus {
  color: #C24141;
}

.table-action-danger:hover,
.table-action-danger:focus {
  background: #FFF1F1;
}

/* 大格子（比赛组）盒子 */
.round-card-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 28px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.03);
  overflow: hidden;
}

.round-card-header {
  background: #ffffff;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #0f172a;
  flex-wrap: wrap;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-badge {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
}

.group-title {
  font-size: 16px;
  font-weight: 600;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-label {
  color: #64748b;
  font-size: 12px;
}

.btn-round-delete {
  color: #ef4444;
  border-color: #fecaca;
}

.btn-round-delete:hover {
  color: #dc2626;
  border-color: #dc2626;
}

/* 大格子内录入表格 */
.round-table-container {
  padding: 0;
  overflow-x: auto;
}

.meta-readonly {
  display: inline-block;
  min-width: 90px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #334155;
  font-size: 13px;
  line-height: 20px;
}

.cell-readonly {
  display: block;
  color: #334155;
  word-break: break-all;
}

.rapid-entry-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.rapid-entry-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  padding: 10px 16px;
  font-size: 13px;
  border-bottom: 1px solid #e2e8f0;
}

.rapid-entry-table td {
  padding: 8px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.row-action-cell {
  text-align: center;
  white-space: nowrap;
}

.drag-handle {
  color: #64748b;
  cursor: move;
  font-size: 16px;
  margin-right: 14px;
}

.drag-handle:hover {
  color: #1890ff;
}

/* 行被标记为犯规或DNS时的底色 */
.row-foul-dns {
  background-color: #fff1f2;
}

/* 名次和差值角标 */
.rank-badge {
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
}
.rank-badge-1 {
  color: #10b981;
}

.gap-time-label {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
}
.gap-best {
  background-color: #d1fae5;
  color: #065f46;
}
.gap-other {
  background-color: #ffedd5;
  color: #9a3412;
}

/* 卡片底部动作栏 */
.round-card-footer {
  background: #fafafa;
  border-top: 1px solid #e2e8f0;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-left {
  font-size: 14px;
  color: #475569;
}

.btn-main-publish {
  background-color: #fff;
  border-color: #cbd5e1;
  border-radius: 6px;
  font-weight: 600;
  color: #334155;
  padding: 0 24px;
}
.btn-main-publish:hover {
  color: #2563eb;
  border-color: #2563eb;
}

/* ---------- 坐舱名单弹窗 ---------- */
.lineup-layout {
  display: flex;
  gap: 20px;
  min-height: 320px;
}

.lineup-team-list {
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid #E4EDF2;
  padding-right: 12px;
  max-height: 420px;
  overflow-y: auto;
}

.lineup-team-item {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.lineup-team-item:hover {
  background: #F4FAFA;
}

.lineup-team-item.active {
  background: #E6F7F5;
  border-color: #4ECDC4;
}

.lineup-team-name {
  font-size: 14px;
  font-weight: 600;
  color: #16232F;
}

.lineup-team-meta {
  font-size: 12px;
  color: #7A8A9A;
  margin-top: 2px;
  display: flex;
  gap: 8px;
}

.lineup-boat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  background: linear-gradient(180deg, #F2FAF9, #E8F4F2);
  border-radius: 10px;
  overflow-y: auto;
}

.boat-head, .boat-tail {
  font-size: 12px;
  color: #7A8A9A;
  letter-spacing: 3px;
  background: #fff;
  border-radius: 14px;
  padding: 3px 20px;
}

.boat-row {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: nowrap;
}

.boat-seat {
  width: 116px;
  padding: 10px 8px;
  background: #fff;
  border: 1px solid #DCEBE8;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(22, 35, 47, 0.06);
}

.boat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4ECDC4, #2FBFB5);
  color: #fff;
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.boat-seat-name {
  font-size: 13px;
  color: #16232F;
  font-weight: 600;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.boat-seat-pos {
  font-size: 11px;
  color: #4ECDC4;
}

.boat-alternates {
  width: 100%;
  max-width: 340px;
  border-top: 1px dashed #E4EDF2;
  margin-top: 8px;
  padding-top: 10px;
}

.boat-alt-title {
  font-size: 12px;
  color: #7A8A9A;
  margin-bottom: 6px;
}

.boat-alt-item {
  font-size: 13px;
  color: #2C3A4B;
  padding: 3px 0;
}

.boat-empty {
  color: #999;
  font-size: 13px;
  padding: 30px 0;
}

.category-language-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  padding: 10px 12px;
  border: 1px solid #DFEAF0;
  border-radius: 8px;
  color: #516572;
  background: #F9FCFD;
  font-size: 13px;
}

.category-language-tabs {
  margin-bottom: 16px;
}

.translation-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.translation-alert {
  margin-bottom: 14px;
}

.form-tip {
  color: #7a8a9a;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 6px;
}
</style>
